/**
 * D-EZ-102 — THE LEAGUE, AS THE STORY SCREENS READ IT.
 *
 * Every story screen (Victory Lane, the newsstand, the tracks, the trophy, the
 * Hall of Fame, the driver's trophy case) reads the same thing: the settled
 * seasons on the shelf plus the one being raced, as `HistorySeason[]`. This is
 * the one door, so no two screens can disagree about what the league holds.
 */

import type { HistorySeason } from '../history/index.ts';
import {
  allRaces,
  buildBoothSeasonContext,
  careerMilestones,
  liveHistorySeason,
  sortedSeasons,
  type Milestone,
} from '../history/index.ts';
import type { RuleEvent } from '../ruleEvent.ts';
import type { BoothSeasonContext } from '../booth/context.ts';
import type { SeasonState } from '../seasonPlay.ts';
import { REGULAR_SEASON_RACES } from '../schedule.ts';

/** The live season, as the shell holds it. */
export interface LiveLeagueSeason {
  seasonNumber: number;
  roster: HistorySeason['roster'];
  teams: HistorySeason['teams'];
  state: SeasonState;
}

/** What the shell can reach: settled seasons, the workbench, the live season. */
export interface LeagueShelf {
  history: readonly HistorySeason[];
  career: HistorySeason | null;
  live?: LiveLeagueSeason | null;
}

/**
 * Every season of the league, oldest first. The live season is included only
 * when no settled season carries its number (a season at race 36 that has
 * since been settled is the settled one).
 */
export function leagueSeasons(shelf: LeagueShelf): HistorySeason[] {
  const byNumber = new Map<number, HistorySeason>();
  for (const s of shelf.history) byNumber.set(s.seasonNumber, s);
  if (shelf.career) byNumber.set(shelf.career.seasonNumber, shelf.career);
  if (shelf.live && !byNumber.has(shelf.live.seasonNumber) && shelf.live.state.races.length > 0) {
    byNumber.set(shelf.live.seasonNumber, liveHistorySeason(shelf.live));
  }
  return sortedSeasons([...byNumber.values()]);
}

/**
 * What the league knew going into race `raceNumber` of the live season —
 * `BoothContext.season` for the booth and the storylines panel. Null outside a
 * season. Built off `stateBeforeRace`, so it can never contain the race itself.
 */
export function seasonContextBefore(shelf: LeagueShelf, raceNumber: number): BoothSeasonContext | null {
  const live = shelf.live;
  if (!live) return null;
  const archive = [...shelf.history, ...(shelf.career ? [shelf.career] : [])].filter(
    (s) => s.seasonNumber !== live.seasonNumber && s.complete !== false,
  );
  try {
    return buildBoothSeasonContext({ archive, live: { ...live, state: stateBeforeRace(live.state, raceNumber) } });
  } catch {
    return null;
  }
}

/**
 * What Victory Lane needs from the league for race `raceNumber` of the live
 * season: the milestones the league recorded IN that race, who won it last
 * time (going in), and that race's slice of the season ledger (the interview).
 */
export function victoryContext(
  shelf: LeagueShelf,
  raceNumber: number,
): { milestones: Milestone[]; defendingWinnerId: string | null; seasonEvents: RuleEvent[] } {
  const live = shelf.live;
  if (!live) return { milestones: [], defendingWinnerId: null, seasonEvents: [] };
  const seasons = leagueSeasons({ ...shelf, live: { ...live, state: { ...live.state, races: live.state.races.filter((r) => r.raceNumber <= raceNumber) } } });
  const priors =
    live.state.inherited?.priors ??
    [...shelf.history, ...(shelf.career ? [shelf.career] : [])].reverse().find((s) => s.offseason?.carry)?.offseason?.carry?.priors ??
    {};
  const milestones = careerMilestones(allRaces(seasons), priors).filter(
    (m) => m.seasonNumber === live.seasonNumber && m.raceNumber === raceNumber,
  );
  return {
    milestones,
    defendingWinnerId: seasonContextBefore(shelf, raceNumber)?.defendingRaceWinner?.driverId ?? null,
    seasonEvents: live.state.races.find((r) => r.raceNumber === raceNumber)?.events ?? [],
  };
}

/** Only the settled seasons, oldest first. */
export function settledSeasons(shelf: LeagueShelf): HistorySeason[] {
  return leagueSeasons({ ...shelf, live: null }).filter((s) => s.complete !== false);
}

/**
 * THE SEASON AS IT STOOD BEFORE RACE `n` WAS RUN.
 *
 * The shell advances the season the moment the green flag drops, so while race
 * n is on the tape `state` already holds its result. Anything the booth or a
 * storyline says ABOUT THE SEASON going into race n must be read off this, or
 * it would know the finish before the finish was shown (D-003).
 */
export function stateBeforeRace(state: SeasonState, n: number): SeasonState {
  const races = state.races.filter((r) => r.raceNumber < n);
  // Who was alive in the bracket going in: the round that contains race n
  // recorded who entered it; a round still open has its entrants as the
  // current contenders; before the bracket is seeded there is none.
  const round = state.playoff.rounds.find((r) => r.races.includes(n));
  const contenders =
    n <= REGULAR_SEASON_RACES ? null : round ? [...round.contenders] : state.playoff.contenders;
  return {
    ...state,
    nextRace: n,
    races,
    standingsAfterRace: state.standingsAfterRace.slice(0, Math.max(0, n - 1)),
    events: state.events.filter((e) => e.raceNumber < n),
    feudThreads: state.feudThreads
      .filter((t) => t.startedRace < n)
      .map((t) => ({
        ...t,
        races: t.races.filter((r) => r < n),
        endedRace: t.endedRace !== null && t.endedRace < n ? t.endedRace : null,
        endedBy: t.endedRace !== null && t.endedRace < n ? t.endedBy : null,
      })),
    fatalities: state.fatalities.filter((f) => f.raceNumber < n),
    rookies: state.rookies.filter((r) => r.raceNumber < n),
    playoff: {
      ...state.playoff,
      rounds: state.playoff.rounds.filter((r) => r.races.every((x) => x < n)),
      contenders,
    },
  };
}
